/**
 * What changed since somebody last looked ("Neuigkeiten").
 *
 * A feature nobody notices was not shipped. This is the list of changes worth
 * telling a person about, shown as cards after signing in (web and app) until
 * they have gone through to the last card — and they accumulate, so somebody
 * away for a month gets all of them at once rather than only the newest.
 * Pattern and lessons from Reiselust (its CLAUDE.md, "The changelog, and help").
 *
 * **Append-only, like a migration.** An entry that has shipped is never
 * edited, reordered or removed: people have acknowledged it by id
 * (dbo.announcement_acks, 0011), and rewriting the text under an id they have
 * dismissed means they never see the correction. A correction is a new entry.
 * `announcements.test.ts` pins the shipped ids in order, and the help lock
 * (packages/data/src/help/help.lock.json) pins each entry's wording.
 *
 * Only what a user can notice belongs here: a new feature, a renamed thing, a
 * bug fix somebody ran into. A refactor, a test or a schema change nobody sees
 * is not news, and a list padded with them teaches people to dismiss it unread.
 *
 * The words live in packages/data/src/news/texts.ts (with the other UI texts
 * the clients share); the server only ever names ids. Here are ids and dates,
 * because the API needs them and the API depends on core alone.
 */

export interface Announcement {
  /**
   * Stable and never reused — this is what an acknowledgement points at.
   * `YYYY-MM-DD-slug`, dated so the order is obvious in the source.
   */
  readonly id: string;
  /** ISO date (Europe/Berlin calendar day), for ordering and for "who joined after". */
  readonly date: string;
}

/** Oldest first. Append at the end, even when an entry is late (see above). */
export const ANNOUNCEMENTS: readonly Announcement[] = [
  { id: '2026-10-04-aussencheck', date: '2026-10-04' },
  { id: '2026-10-04-member-participation', date: '2026-10-04' },
  { id: '2026-10-04-help', date: '2026-10-04' },
  { id: '2026-10-04-demo-tour', date: '2026-10-04' },
  { id: '2026-10-04-push', date: '2026-10-04' },
  { id: '2026-10-04-app-tabs-signout', date: '2026-10-04' },
  { id: '2026-10-04-placement-loupe', date: '2026-10-04' },
  { id: '2026-10-05-view-innen', date: '2026-10-05' },
  { id: '2026-10-05-comment-delete', date: '2026-10-05' },
  { id: '2026-10-05-vf-email-confirm', date: '2026-10-05' },
  { id: '2026-10-05-profile-pictures', date: '2026-10-05' },
  { id: '2026-10-06-email-only-login', date: '2026-10-06' },
  { id: '2026-10-06-avatar-crop', date: '2026-10-06' },
  { id: '2026-10-06-plan-one-finger', date: '2026-10-06' },
  { id: '2026-10-06-welcome-tour', date: '2026-10-06' },
  { id: '2026-10-06-view-counts', date: '2026-10-06' },
  { id: '2026-10-08-fleet-open', date: '2026-10-08' },
  { id: '2026-10-08-status-akzeptiert', date: '2026-10-08' },
  { id: '2026-10-09-no-rear-view', date: '2026-10-09' },
  { id: '2026-10-12-media-required', date: '2026-10-12' },
  { id: '2026-10-13-videos', date: '2026-10-13' },
  { id: '2026-10-13-video-sound', date: '2026-10-13' },
  { id: '2026-10-13-club-logo', date: '2026-10-13' },
  { id: '2026-10-13-background-send', date: '2026-10-13' },
  { id: '2026-10-15-invitations', date: '2026-10-15' },
  { id: '2026-10-17-new-address', date: '2026-10-17' },
  { id: '2026-10-17-media-later', date: '2026-10-17' },
  { id: '2026-10-17-several-categories', date: '2026-10-17' },
  { id: '2026-10-17-standpoint', date: '2026-10-17' },
  { id: '2026-10-17-move-damage', date: '2026-10-17' },
  { id: '2026-10-17-aircraft-notices', date: '2026-10-17' },
  { id: '2026-10-18-ipad', date: '2026-10-18' },
  { id: '2026-10-18-notices-in-app', date: '2026-10-18' },
  { id: '2026-10-18-android-tablets', date: '2026-10-18' },
  { id: '2026-10-24-change-email', date: '2026-10-24' },
  { id: '2026-10-28-further-addresses', date: '2026-10-28' },
  { id: '2026-11-01-catalogue-types', date: '2026-11-01' },
];

/** `YYYY-MM-DD-` followed by a lowercase slug; also the database CHECK (0011). */
export const ANNOUNCEMENT_ID_PATTERN = /^\d{4}-\d{2}-\d{2}-[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** Column width of dbo.announcement_acks.announcement_id. */
export const ANNOUNCEMENT_ID_MAX = 80;

export function isKnownAnnouncement(id: unknown): id is string {
  return typeof id === 'string' && ANNOUNCEMENTS.some((a) => a.id === id);
}

/** The calendar day a timestamp falls on in Germany, as YYYY-MM-DD. */
export function berlinDay(at: Date): string {
  // en-CA formats as YYYY-MM-DD; the time zone is what makes 23:30 UTC the next day in summer.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
}

/**
 * What this person has not seen yet, oldest first.
 *
 * Pure: the caller supplies what was acknowledged and when the person's
 * account was created. **Somebody who joined after an entry starts having seen
 * it** (Reiselust's `announcementsSeenAtSignup`, expressed as a date instead of
 * rows written at sign-up — accounts are created in five places here: invite,
 * CSV import, first admin of a club, added admin, seed): greeting a new member
 * with changes to a product they have never used is noise, and it trains them
 * to dismiss the list unread. An entry dated on the day the account was
 * created counts as seen as well; the day is the only clock both have.
 */
export function pendingAnnouncements(
  acknowledged: readonly string[],
  memberCreatedAt: Date | null,
): readonly Announcement[] {
  const seen = new Set(acknowledged);
  const joined = memberCreatedAt ? berlinDay(memberCreatedAt) : null;
  return ANNOUNCEMENTS.filter((a) => !seen.has(a.id) && (joined === null || a.date > joined));
}

/**
 * The ids of a request to acknowledge that are worth storing: known entries,
 * once each. Unknown ids are dropped, not refused — a client built after the
 * server (or before it) may name an id this build does not have, and a stray
 * row would match nothing forever. `null` means the body was not a list of
 * strings at all (400).
 */
export function acknowledgeableIds(input: unknown): string[] | null {
  if (!Array.isArray(input) || input.length > 200) return null;
  if (input.some((id) => typeof id !== 'string')) return null;
  return [...new Set(input as string[])].filter(isKnownAnnouncement);
}
